import { useEffect, useRef } from "react";
import { FieldValues, UseFormReturn, useWatch } from "react-hook-form";
import { useSearchParams } from "react-router";

export interface UrlSyncedFormConfig<TForm extends FieldValues> {
    /** react-hook-form context whose values mirror the URL search params. */
    formContext: UseFormReturn<TForm>;
    /** Stable string key derived from the current URL state — typically a `|`-joined tuple. */
    urlKey: string;
    /**
     * Stable string key derived from the current form values, comparable to
     * `urlKey`. Values are `Partial<TForm>` because `useWatch` may emit before
     * every field has settled — guard with `?.` / `??`.
     */
    formKey: (values: Partial<TForm>) => string;
    /**
     * Copy URL state into the form (typically `setValue` calls). Return `true`
     * if applied; `false` to defer (e.g. waiting for an async lookup). When
     * deferred, the effect retriggers on any change in `extraDeps`.
     */
    applyUrlToForm: () => boolean;
    /** Build the next URL params from the current form values. */
    applyFormToUrl: (values: Partial<TForm>, prev: URLSearchParams) => URLSearchParams;
    /** Gate; both directions skip while false. Defaults to true. */
    hydrated?: boolean;
    /** Extra deps that should retrigger the URL→form sync (typically async-readiness flags). */
    extraDeps?: ReadonlyArray<unknown>;
}

/**
 * Bidirectional sync between react-hook-form state and `?search` params.
 *
 * Pattern: a single `lastSyncedKey` ref records whichever side (URL or form)
 * last wrote. Each direction skips when its computed key matches the ref,
 * breaking the feedback loop. `hydrated` gates both directions until any async
 * hydration the caller needs to perform (e.g. fetching a lookup by code from
 * the URL) has completed.
 */
export default function useUrlSyncedForm<TForm extends FieldValues>(
    config: UrlSyncedFormConfig<TForm>,
) {
    const [, setUrlParams] = useSearchParams();
    const { formContext, urlKey, hydrated = true, extraDeps = [] } = config;

    // Callbacks read through a ref so effect deps stay tight.
    const cfgRef = useRef(config);
    cfgRef.current = config;

    // Initialized to URL's mount-time key so the very first form→URL effect
    // (form defaults may still be out of sync with URL until hydration) is a no-op.
    const lastSyncedKey = useRef(urlKey);

    // URL → form
    useEffect(() => {
        if (!hydrated) return;
        if (urlKey === lastSyncedKey.current) return;
        if (cfgRef.current.applyUrlToForm()) {
            lastSyncedKey.current = urlKey;
        }
    }, [hydrated, urlKey, ...extraDeps]);

    const values = useWatch({ control: formContext.control }) as Partial<TForm>;

    // form → URL
    useEffect(() => {
        if (!hydrated) return;
        const key = cfgRef.current.formKey(values);
        if (key === lastSyncedKey.current) return;
        lastSyncedKey.current = key;
        setUrlParams((prev) => cfgRef.current.applyFormToUrl(values, prev), {
            replace: true,
        });
    }, [hydrated, values, setUrlParams]);

    return { setUrlParams };
}
